import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

function IncidentalLoanTopup() {
  const navigate = useNavigate()
  const [loans, setLoans] = useState([])
  const [selectedLoan, setSelectedLoan] = useState(null)
  const [formData, setFormData] = useState({
    noPinjaman: '',
    jumlahTopup: 0,
    dikenakanAdmin: false,
    catatan: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const loanSelectRef = useRef(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [namaKoperasi, setNamaKoperasi] = useState('Koperasi')
  const [settings, setSettings] = useState(null)
  const [topupPreview, setTopupPreview] = useState(null)

  useEffect(() => {
    fetchActiveLoans()
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await api.get('/incidental-loans/settings')
      if (response.data?.success) {
        const settingsArray = response.data.data
        if (Array.isArray(settingsArray) && settingsArray.length > 0) {
          setSettings(settingsArray[0])
        } else {
          setSettings(settingsArray)
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  useEffect(() => {
    if (loanSelectRef.current && loans.length > 0) {
      $(loanSelectRef.current).select2({
        placeholder: '-- Pilih Pinjaman Insidentil --',
        allowClear: true,
        width: '100%'
      })

      $(loanSelectRef.current).on('change', (e) => {
        const noPinjaman = $(e.target).val()
        handleLoanChange(noPinjaman)
      })

      return () => {
        if (loanSelectRef.current) {
          $(loanSelectRef.current).off('change').select2('destroy')
        }
      }
    }
  }, [loans])

  const fetchActiveLoans = async () => {
    try {
      const response = await api.get('/incidental-loans?status=Dicairkan')
      setLoans(response.data.data || [])
    } catch (error) {
      console.error('Error fetching loans:', error)
    }
  }

  const handleLoanChange = async (noPinjaman) => {
    const selected = loans.find(l => l.NoPinjaman === noPinjaman)
    setSelectedLoan(selected)

    if (selected) {
      setFormData({
        ...formData,
        noPinjaman
      })
      calculateTopupPreview(selected)
    } else {
      setSelectedLoan(null)
      setTopupPreview(null)
    }
  }

  const calculateTopupPreview = (loan, currentFormData = formData) => {
    if (!settings) return

    const jumlahTopup = parseFloat(currentFormData.jumlahTopup) || 0
    const sisaPokok = parseFloat(loan.SisaPokok) || 0
    const maxTopup = settings.Plafon - sisaPokok

    const adminFee = currentFormData.dikenakanAdmin ? (jumlahTopup * (settings.PersentaseAdmin / 100)) : 0
    const jumlahDiterimaAnggota = jumlahTopup - adminFee

    setTopupPreview({
      jumlahTopup,
      maxTopup,
      adminFee,
      totalJumlah: jumlahTopup,
      jumlahDiterimaAnggota,
      newSisaPokok: sisaPokok + jumlahTopup,
      newSisaTenor: settings.JangkaWaktu
    })
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value

    const updatedFormData = {
      ...formData,
      [name]: newValue
    }

    setFormData(updatedFormData)

    if (selectedLoan) {
      calculateTopupPreview(selectedLoan, updatedFormData)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!selectedLoan) {
      setError('Silakan pilih pinjaman terlebih dahulu')
      return
    }

    if (!formData.jumlahTopup || formData.jumlahTopup <= 0) {
      setError('Jumlah topup harus lebih dari 0')
      return
    }

    const maxTopup = settings.Plafon - selectedLoan.SisaPokok
    if (formData.jumlahTopup > maxTopup) {
      setError(`Topup maksimal adalah ${maxTopup.toLocaleString('id-ID')} (plafon - sisa pokok)`)
      return
    }

    setLoading(true)

    try {
      const response = await api.post(`/incidental-loans/${selectedLoan.NoPinjaman}/topup`, {
        JumlahTopup: formData.jumlahTopup,
        DikenakanAdmin: formData.dikenakanAdmin,
        Catatan: formData.catatan
      })

      if (response.data?.success) {
        setPrintData({
          ...response.data.data,
          loan: selectedLoan,
          formData: { ...formData }
        })
        setShowPrintModal(true)
        fetchActiveLoans()
      } else {
        setError(response.data?.message || 'Gagal melakukan topup')
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Gagal melakukan topup')
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    const printContent = document.getElementById('print-content')
    if (printContent) {
      const originalContent = document.body.innerHTML
      document.body.innerHTML = printContent.innerHTML
      window.print()
      document.body.innerHTML = originalContent
      window.location.reload()
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Topup Pinjaman Insidentil</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><a href="/incidental-loans">Pinjaman Insidentil</a></li>
                <li className="breadcrumb-item active">Topup</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Form Topup Pinjaman Insidentil</h3>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Pilih Pinjaman Insidentil *</label>
                      <select
                        ref={loanSelectRef}
                        className="form-control select2"
                        style={{ width: '100%' }}
                      >
                        <option value="">-- Pilih Pinjaman Insidentil --</option>
                        {loans.map(loan => (
                          <option key={loan.NoPinjaman} value={loan.NoPinjaman}>
                            {loan.NBA} - {loan.Nama}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {selectedLoan && (
                  <>
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <div className="card card-info card-outline">
                          <div className="card-header">
                            <h3 className="card-title">Informasi Pinjaman</h3>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-4">
                                <p><strong>NBA:</strong> {selectedLoan.NBA}</p>
                                <p><strong>Nama:</strong> {selectedLoan.Nama}</p>
                              </div>
                              <div className="col-md-4">
                                <p><strong>Jumlah Dicairkan:</strong> {formatCurrency(selectedLoan.JumlahDicairkan)}</p>
                                <p><strong>Sisa Pokok:</strong> {formatCurrency(selectedLoan.SisaPokok)}</p>
                              </div>
                              <div className="col-md-4">
                                <p><strong>Plafon Maksimal:</strong> {formatCurrency(settings?.Plafon || 0)}</p>
                                <p><strong>Maksimal Topup:</strong> {formatCurrency(settings?.Plafon - selectedLoan.SisaPokok || 0)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Jumlah Topup *</label>
                          <input
                            type="number"
                            name="jumlahTopup"
                            className="form-control"
                            value={formData.jumlahTopup}
                            onChange={handleInputChange}
                            min="100000"
                            step="1000"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>
                            <input
                              type="checkbox"
                              name="dikenakanAdmin"
                              checked={formData.dikenakanAdmin}
                              onChange={handleInputChange}
                            />{' '}
                            Dikenakan Admin Fee
                          </label>
                          {formData.dikenakanAdmin && settings && (
                            <p className="text-muted small">
                              {settings.PersentaseAdmin}% dari jumlah topup
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label>Catatan</label>
                          <textarea
                            name="catatan"
                            className="form-control"
                            rows="3"
                            value={formData.catatan}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>

                    {topupPreview && (
                      <div className="row mt-3">
                        <div className="col-md-12">
                          <div className="card card-success card-outline">
                            <div className="card-header">
                              <h3 className="card-title">Perhitungan Topup</h3>
                            </div>
                            <div className="card-body">
                              <div className="row">
                                <div className="col-md-6">
                                  <p><strong>Jumlah Topup:</strong> {formatCurrency(topupPreview.jumlahTopup)}</p>
                                  {formData.dikenakanAdmin && (
                                    <p><strong>Admin Fee ({settings?.PersentaseAdmin}%):</strong> {formatCurrency(topupPreview.adminFee)}</p>
                                  )}
                                </div>
                                <div className="col-md-6">
                                  <p><strong>Jumlah Diterima Anggota:</strong> {formatCurrency(topupPreview.jumlahDiterimaAnggota !== undefined ? topupPreview.jumlahDiterimaAnggota : (topupPreview.jumlahTopup - topupPreview.adminFee))}</p>
                                  <p><strong>Sisa Pokok Setelah Topup:</strong> {formatCurrency(topupPreview.newSisaPokok)}</p>
                                  <p><strong>Sisa Tenor Setelah Topup:</strong> {topupPreview.newSisaTenor} bulan</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row mt-3">
                      <div className="col-md-12">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Memproses...' : 'Proses Topup'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default ml-2"
                          onClick={() => navigate('/incidental-loans')}
                        >
                          Kembali
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {showPrintModal && printData && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Bukti Topup Pinjaman Insidentil</h4>
                  <button type="button" className="close" onClick={() => setShowPrintModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div id="print-content">
                    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
                      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <h2>{namaKoperasi}</h2>
                        <h3>Bukti Topup Pinjaman Insidentil</h3>
                        <p>No. Jurnal: {printData.NoJurnal}</p>
                        <p>Tanggal: {new Date().toLocaleString('id-ID')}</p>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <h4>Informasi Anggota</h4>
                        <p><strong>NBA:</strong> {printData.loan.NBA}</p>
                        <p><strong>Nama:</strong> {printData.loan.Nama}</p>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <h4>Informasi Pinjaman</h4>
                        <p><strong>No. Pinjaman:</strong> {printData.loan.NoPinjaman}</p>
                        <p><strong>Sisa Pokok Sebelum:</strong> {formatCurrency(printData.loan.SisaPokok)}</p>
                        <p><strong>Jumlah Topup:</strong> {formatCurrency(formData.jumlahTopup)}</p>
                        {formData.dikenakanAdmin && (
                          <p><strong>Admin Fee:</strong> {formatCurrency(printData.AdminFee)}</p>
                        )}
                        <p><strong>Jumlah Diterima Anggota:</strong> {formatCurrency(printData.JumlahDiterimaAnggota || (parseFloat(formData.jumlahTopup) - parseFloat(printData.AdminFee || 0)) || printData.TotalJumlah)}</p>
                        <p><strong>Sisa Pokok Setelah:</strong> {formatCurrency(printData.SisaPokok)}</p>
                        <p><strong>Sisa Tenor Setelah:</strong> {printData.SisaTenor} bulan</p>
                      </div>

                      {formData.catatan && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4>Catatan</h4>
                          <p>{formData.catatan}</p>
                        </div>
                      )}

                      <div style={{ marginTop: '40px', textAlign: 'center' }}>
                        <p>Terima kasih atas kepercayaan Anda.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={handlePrint}>
                    <i className="fas fa-print mr-1"></i> Cetak
                  </button>
                  <button type="button" className="btn btn-default" onClick={() => setShowPrintModal(false)}>
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default IncidentalLoanTopup
